import { Avatar, Box, Button, Grid, Typography } from '@mui/material';
import { ArrowForwardIos, CheckRounded, EditNoteOutlined, ReceiptLongOutlined, TaskAltOutlined, SendOutlined } from '@mui/icons-material';
import { keyframes } from '@emotion/react';
import heroImage from 'src/assets/images/fs-hero-template.png';
import nordicMap from 'src/assets/images/nordic-map.png';
import recipient1 from 'src/assets/images/recipient-1.jpg';
import recipient2 from 'src/assets/images/recipient-2.jpg';
import recipient3 from 'src/assets/images/recipient-3.jpg';
import recipient4 from 'src/assets/images/recipient-4.jpg';
import recipient5 from 'src/assets/images/recipient-5.jpg';

interface Props {
  onStart: () => void;
}

const flowSteps = [
  { title: 'Följ guiden för att beställa', Icon: EditNoteOutlined },
  { title: 'Vår personal skapar alla fakturor', Icon: ReceiptLongOutlined },
  { title: 'Ni granskar och godkänner utskicket', Icon: TaskAltOutlined },
  { title: 'Fakturorna skickas ut på valt utskicksdatum', Icon: SendOutlined },
];

// Fakturor "glider" ut ur laptop-skärmen (origin ~60%/56%) upp mot mottagarna. Fast referens-storlek på "scenen"
// gör att vi kan animera enbart transform (GPU-kompositerat → mjukt och skarpt, inte hackigt/suddigt som left/top).
const STAGE_W = 400;
const STAGE_H = Math.round((STAGE_W * 534) / 406); // behåller mallens proportioner (406x534)
const ORIGIN = { x: 0.6, y: 0.56 };
const recipients = [
  { x: 0.54, y: 0.37, img: recipient1 },
  { x: 0.63, y: 0.22, img: recipient2 },
  { x: 0.75, y: 0.13, img: recipient3 },
  { x: 0.87, y: 0.21, img: recipient4 },
  { x: 0.94, y: 0.38, img: recipient5 },
];

const flyTo = (dx: number, dy: number) => keyframes`
  0%   { transform: translate(-50%, -50%) scale(0.9); opacity: 0; }
  12%  { opacity: 1; }
  82%  { opacity: 1; }
  100% { transform: translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(1); opacity: 0; }
`;

const CYCLE = 6;
const envelopes = recipients.map((r, i) => ({
  anim: flyTo(Math.round((r.x - ORIGIN.x) * STAGE_W), Math.round((r.y - ORIGIN.y) * STAGE_H)),
  delay: (i * CYCLE) / recipients.length,
}));

// Miniatyr-faktura (stiliserad efter ett fakturamail): avsändarrad, rubrik, textrader och mörk "betala"-knapp.
const InvoiceMini = () => (
  <>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
      <Box sx={{ width: 5, height: 5, borderRadius: '1px', bgcolor: 'primary.main', flexShrink: 0 }} />
      <Box sx={{ height: 1.5, borderRadius: 1, bgcolor: 'grey.400', flexGrow: 1 }} />
    </Box>
    <Box sx={{ height: 2.5, width: '75%', borderRadius: 1, bgcolor: 'grey.600' }} />
    <Box sx={{ height: 1.5, width: '100%', borderRadius: 1, bgcolor: 'grey.300' }} />
    <Box sx={{ height: 1.5, width: '85%', borderRadius: 1, bgcolor: 'grey.300' }} />
    <Box sx={{ mt: 'auto', height: 6, borderRadius: 0.75, bgcolor: 'grey.800' }} />
  </>
);

// Motiv på laptop-skärmen: en checklista som bockas i rad för rad (systemet skapar/förbereder fakturorna).
const SCREEN_CYCLE = 4.8;
const checkIn = keyframes`
  0%   { opacity: 0; transform: scale(0.3); }
  8%   { opacity: 1; transform: scale(1); }
  82%  { opacity: 1; transform: scale(1); }
  92%  { opacity: 0; transform: scale(0.3); }
  100% { opacity: 0; }
`;
const checklistRows = [{ w: '100%' }, { w: '84%' }, { w: '68%' }];

const ScreenChecklist = () => (
  <Box
    sx={{
      position: 'absolute',
      left: '54%',
      top: '66%',
      transform: 'translate(-50%, -50%) rotate(-10deg)',
      width: '24%',
      height: '12.5%',
      px: '6%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      gap: '14%',
    }}
  >
    {checklistRows.map((row, i) => (
      <Box key={row.w} sx={{ display: 'flex', alignItems: 'center', gap: '6%' }}>
        <Box sx={{ position: 'relative', width: 9, height: 9, flexShrink: 0 }}>
          <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '1px solid', borderColor: 'grey.400' }} />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              bgcolor: 'success.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0,
              animation: `${checkIn} ${SCREEN_CYCLE}s ease-in-out ${i * 0.55}s infinite`,
              '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 1 },
            }}
          >
            <CheckRounded sx={{ fontSize: 7, color: '#fff' }} />
          </Box>
        </Box>
        <Box sx={{ height: 2, borderRadius: 1, bgcolor: 'grey.300', width: row.w }} />
      </Box>
    ))}
  </Box>
);

export const SendingWizardIntro = ({ onStart }: Props) => (
  <Grid container spacing={4} alignItems='center'>
    <Grid item xs={12} md={7}>
      <Box sx={{ pl: { xs: 0, md: 6 } }}>
        <Typography variant='h4' sx={{ fontWeight: 700, mb: 1.5 }}>
          Välkommen till vår utskicksservice
        </Typography>
        <Typography variant='body1' sx={{ mb: 3, color: 'text.secondary', fontSize: '1.15rem', lineHeight: 1.6 }}>
          Beställ fakturautskick för att få hjälp med att skapa och skicka ut medlemsfakturor till era medlemmar.
        </Typography>

        <Box sx={{ mb: 3, mt: 1 }}>
          {flowSteps.map((step, i) => (
            <Box key={step.title} sx={{ display: 'flex', gap: 2, position: 'relative', pb: i < flowSteps.length - 1 ? 3 : 0 }}>
              {i < flowSteps.length - 1 && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: 17,
                    top: 34,
                    bottom: 2,
                    borderLeft: '2px dashed',
                    borderColor: 'grey.400',
                  }}
                />
              )}
              <Box
                sx={{
                  flexShrink: 0,
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  bgcolor: 'background.paper',
                  border: '1.5px solid',
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1,
                }}
              >
                <step.Icon sx={{ fontSize: 20 }} />
              </Box>
              <Typography variant='body1' sx={{ fontWeight: 500, pt: 0.75 }}>
                {step.title}
              </Typography>
            </Box>
          ))}
        </Box>

        <Button variant='contained' disableElevation onClick={onStart} endIcon={<ArrowForwardIos fontSize='small' />}>
          Påbörja en beställning
        </Button>
      </Box>
    </Grid>

    {/* Frilagd hjälte (gubbe + laptop) med animerade fakturor som skickas ut ur skärmen till flera mottagare. */}
    <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
      <Box sx={{ position: 'relative', width: STAGE_W, maxWidth: '100%' }}>
        <Box component='img' src={heroImage} alt='' sx={{ width: '100%', display: 'block' }} />

        {/* Nordisk karta i bakgrunden – illustrerar att fakturorna går till geografiskt utspridda medlemmar.
            Avatarerna nedan hamnar ovanpå som platsmarkörer. */}
        <Box
          component='img'
          src={nordicMap}
          alt=''
          aria-hidden
          sx={{
            position: 'absolute',
            left: '72%',
            top: '26%',
            transform: 'translate(-50%, -50%)',
            height: '50%',
            width: 'auto',
            opacity: 0.38,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />

        {/* Motiv på laptop-skärmen */}
        <ScreenChecklist />

        {/* Mottagare (person-avatarer) */}
        {recipients.map((r) => (
          <Avatar
            key={`${r.x}-${r.y}`}
            src={r.img}
            alt=''
            sx={{
              position: 'absolute',
              left: `${r.x * 100}%`,
              top: `${r.y * 100}%`,
              width: 42,
              height: 42,
              transform: 'translate(-50%, -50%)',
              border: '2.5px solid #fff',
              boxShadow: 2,
            }}
          />
        ))}

        {/* Fakturor på väg ut (mini-fakturakort) */}
        {envelopes.map((e, i) => (
          <Box
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            sx={{
              position: 'absolute',
              left: `${ORIGIN.x * 100}%`,
              top: `${ORIGIN.y * 100}%`,
              width: 34,
              height: 44,
              transform: 'translate(-50%, -50%)',
              borderRadius: 0.75,
              bgcolor: '#fff',
              boxShadow: 3,
              p: '3px',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              overflow: 'hidden',
              opacity: 0,
              willChange: 'transform, opacity',
              backfaceVisibility: 'hidden',
              animation: `${e.anim} ${CYCLE}s ease-in-out ${e.delay}s infinite`,
              '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0 },
            }}
          >
            <InvoiceMini />
          </Box>
        ))}
      </Box>
    </Grid>
  </Grid>
);
